'use client';

import { useBunja } from 'bunja/react';
import { formatISO } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import UserCautionColorIcon from '@flipster/react/icon/generated/UserCautionColorIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { formatUTCTimeZone } from '@flipster/utils/datetime';
import segment, { KycEvent } from '@flipster/utils/segment';

import { kycReminderBunja } from './bunja';

interface KycPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

const KycPopup = ({ isOpen, onClose, onExit }: KycPopupProps) => {
  const t = useTranslations();
  const { kycStatusAtom, kycModalHideTimestampAtom } = useBunja(kycReminderBunja);
  const kycInfo = useAtomValue(kycStatusAtom);
  const setKycModalHideTimestamp = useSetAtom(kycModalHideTimestampAtom);

  const { status, comment, level } = useMemo(() => {
    if (!kycInfo) {
      return { status: null, comment: null, level: null };
    }

    const {
      gracePeriod,
      level2SimpleStatus,
      level2Status,
      level3Status,
      level4Status,
      level2SimpleComment,
      level2Comment,
      level3Comment,
      level4Comment,
    } = kycInfo;

    // gracePeriod가 null일 경우 처리
    if (!gracePeriod) {
      return { status: null, comment: null, level: null };
    }

    // gracePeriod에 해당하는 레벨 이름과 상태를 확인
    const { levelName } = gracePeriod;
    let status = null;
    let comment = null;

    // gracePeriod에 해당하는 레벨 상태와 코멘트 추출
    switch (levelName) {
      case 'LEVEL_2_SIMPLE':
        status = level2SimpleStatus;
        comment = level2SimpleComment;
        break;
      case 'LEVEL_2':
        status = level2Status;
        comment = level2Comment;
        break;
      case 'LEVEL_3':
        status = level3Status;
        comment = level3Comment;
        break;
      case 'LEVEL_4':
        status = level4Status;
        comment = level4Comment;
        break;
      default:
        return { status: null, comment: null, level: null }; // 정의되지 않은 레벨의 경우
    }

    // 상태가 RESUBMIT 또는 REJECTED인 경우 상태와 코멘트를 반환
    if (status === 'RESUBMIT' || status === 'REJECTED') {
      const formattedStatus = status === 'REJECTED' ? t('Kyc.r250311.Rejected') : t('Kyc.r250311.Resubmit_title');
      const formattedLevel = t('Common.r250922.Level_level', { level: levelName.split('_')[1] });

      return { status: formattedStatus, comment, level: formattedLevel };
    }

    // 해당 조건에 맞는 상태가 없으면 null 반환
    return { status: null, comment: null, level: null };
  }, [kycInfo, t]);

  const handleCloseModal = () => {
    setKycModalHideTimestamp(formatISO(new Date()));
    onClose();
  };

  const handleClickVerifyNow = () => {
    segment.track(KycEvent.KycPopUpClick, { kyc_review_type: status });
    handleCloseModal();
  };

  useEffect(() => {
    if (kycInfo) {
      segment.track(KycEvent.KycPopUpView, { kyc_review_type: status });
    }
  }, [kycInfo, status]);

  if (!kycInfo) return null;

  return (
    <Modal.Root isOpen={isOpen} size='large' onClose={handleCloseModal} onExit={onExit}>
      <Modal.Header>
        <div className={flex({ align: 'center', gap: 8, tablet: { gap: 12 } })}>
          <UserCautionColorIcon className={css({ w: 24, h: 24, tablet: { w: 32, h: 32 } })} />
          <Modal.Title label={<FormattedMessage id='Market.r250922.Kyc_required' />} />
        </div>
      </Modal.Header>

      <Modal.Content>
        <p className={css({ textStyle: 'Text/L/Regular', mb: 24 })}>
          <span className={css({ tablet: { display: 'block' } })}>
            <FormattedMessage id='Market.r250922.Your_document_needs_resubmission' />
          </span>
          <span className={css({ color: 'inverse.white' })}>
            <FormattedMessage
              id='Market.r250922.Provide_updated_documents_by'
              values={{
                date: formatUTCTimeZone(kycInfo.gracePeriod?.finishAt, 'yy.MM.dd'),
                time: formatUTCTimeZone(kycInfo.gracePeriod?.finishAt, 'HH:mm'),
              }}
            />
          </span>
        </p>
        <div className={css({ p: 16, bg: 'background.default.level-3', rounded: 4 })}>
          <div className={flex({ align: 'center', justify: 'space-between', mb: 8 })}>
            <span className={flex({ align: 'center', gap: 4, color: 'content.state.negative' })}>
              <CautionCircleIcon size={16} />
              <span className={css({ textStyle: 'Text/L/Strong' })}>{status}</span>
            </span>
            <span className={css({ textStyle: 'Text/L/Strong' })}>{level}</span>
          </div>
          <p className={css({ textStyle: 'Text/M/Regular', whiteSpace: 'pre-line' })}>{comment}</p>
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Modal.Button
          size='large'
          hierarchy='primary'
          label={<FormattedMessage id='Common.r250922.Verify_now' />}
          as='a'
          href='/user/kyc'
          onClick={handleClickVerifyNow}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};

export default KycPopup;
